// src/RegisterStore.js
import React, { useEffect, useMemo, useState } from "react";
import { api, API_BASE, setStoreIdHeader, setUserHeader } from "./api";
import { useNavigate } from "react-router-dom";

const POS_TYPES = [
  { value: "retail", label: "Retail" },
  { value: "hotel", label: "Hotel" },
  { value: "restaurant", label: "Restaurant" },
  { value: "pharmacy", label: "Pharmacy" },
  { value: "salon", label: "Salon" },
  { value: "grocery", label: "Grocery" },
  { value: "bar", label: "Bar" },
];

export default function RegisterStore({ onSuccess }) {
  const navigate = useNavigate();

  const [storeName, setStoreName] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [email, setEmail] = useState("");
  const [adminUsername, setAdminUsername] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [posType, setPosType] = useState("retail");

  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    const username = localStorage.getItem("username");
    const userStr = localStorage.getItem("user") || "{}";

    if (!username) {
      alert("You must login as super_user first.");
      navigate("/", { replace: true });
      return;
    }

    let userObj = {};
    try {
      userObj = JSON.parse(userStr);
    } catch {
      userObj = {};
    }

    const role = (
      userObj?.role ||
      userObj?.user_level ||
      userObj?.level ||
      ""
    )
      .toString()
      .toLowerCase();

    if (role !== "super_user") {
      alert("Access denied. Only super_user can register a store.");
      navigate("/dashboard", { replace: true });
      return;
    }

    setUserHeader(username);
    setStoreIdHeader(0);
  }, [navigate]);

  const passwordMismatch = useMemo(() => {
    if (!confirmPassword) return false;
    return adminPassword !== confirmPassword;
  }, [adminPassword, confirmPassword]);

  const validate = () => {
    if (!storeName.trim()) return "Store name is required";
    if (!email.trim()) return "Owner email is required";
    if (!adminUsername.trim()) return "Admin username is required";
    if (adminPassword.length < 6) return "Password must be at least 6 characters";
    if (adminPassword !== confirmPassword) return "Passwords do not match";
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setMsg("");

    const err = validate();
    if (err) {
      setMsg("⚠️ " + err);
      return;
    }

    try {
      setBusy(true);

      const res = await api.post("/server.php/register-store", {
        store_name: storeName.trim(),
        owner_name: ownerName.trim() || null,
        email: email.trim(),
        username: adminUsername.trim(),
        password: adminPassword,
        pos_type: posType,
      });

      if (!res.data?.success) {
        setMsg("❌ " + (res.data?.message || "Registration failed"));
        return;
      }

      const sid = Number(res.data?.store_id);
      if (!sid) {
        setMsg("❌ Store created but no ID returned");
        return;
      }

      localStorage.setItem("store_id", String(sid));
      localStorage.setItem("store_type", String(posType).toLowerCase());
      localStorage.setItem("saved_store_id", String(sid));
      localStorage.setItem("saved_username", adminUsername.trim());

      setStoreIdHeader(sid);

      const loginRes = await api.post("/server.php/login", {
        store_id: sid,
        username: adminUsername.trim(),
        password: adminPassword,
      });

      if (loginRes.data?.success) {
        const userObj = loginRes.data?.user || {};
        const finalUsername = String(userObj?.username || adminUsername.trim());
        const finalStoreId = Number(
          loginRes.data?.store_id || userObj?.store_id || sid
        );

        const finalStoreType = String(
          loginRes.data?.store_type || userObj?.store_type || posType
        ).toLowerCase();

        localStorage.setItem("store_id", String(finalStoreId));
        localStorage.setItem("store_type", finalStoreType);
        localStorage.setItem("username", finalUsername);
        localStorage.setItem("user", JSON.stringify(userObj));

        if (userObj?.role) {
          localStorage.setItem("role", String(userObj.role).toLowerCase());
        }

        setStoreIdHeader(finalStoreId);
        setUserHeader(finalUsername);

        window.location.href = "/dashboard";
        return;
      }

      setMsg("✅ Store created. Please login manually.");
      onSuccess?.(sid, res.data);
    } catch (err2) {
      const serverMsg =
        err2?.response?.data?.message ||
        err2?.message ||
        "Server error";
      setMsg(`❌ ${serverMsg}`);
    } finally {
      setBusy(false);
    }
  };

  const inputType = showPassword ? "text" : "password";

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h2 style={styles.title}>🏪 Register Store</h2>
        <div style={styles.api}>
          API: <code>{API_BASE}</code>
        </div>

        <form onSubmit={submit} style={styles.form}>
          <input
            style={styles.input}
            placeholder="Store name"
            value={storeName}
            onChange={(e) => setStoreName(e.target.value)}
            disabled={busy}
          />

          <input
            style={styles.input}
            placeholder="Owner name (optional)"
            value={ownerName}
            onChange={(e) => setOwnerName(e.target.value)}
            disabled={busy}
          />

          <input
            style={styles.input}
            placeholder="Owner email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
          />

          <input
            style={styles.input}
            placeholder="Admin username"
            value={adminUsername}
            onChange={(e) => setAdminUsername(e.target.value)}
            disabled={busy}
          />

          <div style={{ position: "relative" }}>
            <input
              type={inputType}
              style={styles.input}
              placeholder="Admin password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              disabled={busy}
            />
            <span
              style={styles.toggle}
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? "Hide" : "Show"}
            </span>
          </div>

          <input
            type={inputType}
            style={{
              ...styles.input,
              borderColor: passwordMismatch ? "#d33" : "#ccc",
            }}
            placeholder="Confirm password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={busy}
          />

          {passwordMismatch && (
            <div style={{ color: "#d33", fontSize: 12 }}>
              Passwords do not match
            </div>
          )}

          <select
            style={styles.input}
            value={posType}
            onChange={(e) => setPosType(e.target.value)}
            disabled={busy}
          >
            {POS_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>

          <button style={styles.button} disabled={busy}>
            {busy ? "Creating..." : "Create Store"}
          </button>
        </form>

        {msg && <div style={styles.msg}>{msg}</div>}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "#f3f4f6",
  },
  card: {
    width: 420,
    background: "#fff",
    padding: 25,
    borderRadius: 12,
    boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
  },
  title: { marginBottom: 5 },
  api: { fontSize: 12, marginBottom: 15, color: "#666" },
  form: { display: "flex", flexDirection: "column", gap: 10 },
  input: {
    padding: 10,
    borderRadius: 6,
    border: "1px solid #ccc",
    fontSize: 14,
  },
  button: {
    padding: 10,
    borderRadius: 6,
    border: "none",
    background: "#2563eb",
    color: "#fff",
    fontWeight: "bold",
    cursor: "pointer",
  },
  toggle: {
    position: "absolute",
    right: 10,
    top: 10,
    fontSize: 12,
    cursor: "pointer",
    color: "#2563eb",
    userSelect: "none",
  },
  msg: { marginTop: 10 },
};